import { CheckIcon } from "@heroicons/react/16/solid";
import classNames from "classnames";
import React, { PropsWithChildren, useState } from "react";
import { twMerge } from "tailwind-merge";

type CheckboxProps = {
  hasError?: boolean;
  className?: string;
} & (
  | { value: undefined; onChange: undefined }
  | { value: boolean; onChange: (newValue: boolean) => void }
);

export const Checkbox: React.FC<PropsWithChildren<CheckboxProps>> = ({
  children,
  className,
  hasError,
  value,
  onChange,
}) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(false);

  const usedValue = value ? value : uncontrolledValue;

  const handleCheckboxClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() === "a") {
      event.stopPropagation();
    } else {
      if (onChange) {
        onChange(!value);
      } else {
        setUncontrolledValue((prev) => !prev);
      }
    }
  };

  return (
    <div
      className={twMerge(
        "flex items-center gap-2 cursor-pointer w-fit",
        className,
      )}
      onClick={handleCheckboxClick}
    >
      <div
        className={classNames(
          "min-w-6 min-h-6 border rounded-full flex justify-center items-center transition-all duration-300",
          {
            "bg-primary border-primary": !!usedValue,
            "bg-white border-gray-400": !usedValue,
            "border-red-600": hasError && !usedValue,
          },
        )}
      >
        {usedValue && (
          <CheckIcon height={16} width={16} className="text-white" />
        )}
      </div>

      <label
        className={classNames(
          "ml-2 text-base font-semibold cursor-pointer",
          { "outline-1 text-red-600": hasError && !usedValue },
          { "text-gray-800": !hasError || !!usedValue },
        )}
      >
        {children}
      </label>
    </div>
  );
};
